/**
 * App.tsx — PIEA AGI — Proof-First Interface
 *
 * Spec compliance: P104 (Identity Rail), P105 (Uncertainty Record),
 * P107 (Outlier / dissent), P136 (Verdict hash display)
 *
 * Design principle: every response leads with cryptographic proof.
 * The substrate is not a chat app. It is a governed reasoning engine
 * that happens to have a conversation interface.
 *
 * Classification: trade-secret/private
 */

import { useState, useEffect, useRef, useCallback, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Landing } from "./Landing";
import {
    getApiKey, setApiKey, getAgiMode, setAgiMode,
    deliberateSync, getSignals, triggerAutonomousCycle,
    getGoals, createGoal, getProjects, createProject, getDeliberationHistory,
    getHealth, startSubstrateSession,
    type SignalsFeed, type AgiMode,
    type SyncDeliberationResult, type Goal, type Project, type DeliberationRecord,
    type IdentityProof, type UncertaintyRecord,
} from "./api";

// -- Types ----------------------------------------------------------------------

type Depth = "CONVERSATIONAL" | "ANALYTICAL" | "DEEP_RESEARCH";
type View = "chat" | "signals" | "goals" | "projects" | "history";

interface Message {
    id: string;
    role: "user" | "agi";
    content: string;
    verdict?: SyncDeliberationResult["verdict"] | null;
    sources?: Array<{ source_url: string; snippet?: string; provenance_class?: string }>;
    confidence?: number;
    ai_powered?: boolean;
    chain_depth?: number;
    duration_ms?: number;
    timestamp: string;
    streaming?: boolean;
    depth?: Depth;
}

interface SubstrateState {
    identity_proof: IdentityProof | null;
    chain_depth: number;
    kernel_version: string;
    lockfile_version: string;
    verified: boolean;
}

// -- Helpers --------------------------------------------------------------------

function hostname(url: string): string {
    try { return new URL(url).hostname.replace("www.", ""); }
    catch { return url.slice(0, 30); }
}

function timeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

function shortHash(h: string): string {
    return h ? `0x${h.slice(0, 8)}…${h.slice(-4)}` : "—";
}

function copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).catch(() => null);
}

// -- Identity Rail (P104) ------------------------------------------------------

function IdentityRail({ substrate }: { substrate: SubstrateState | null }) {
    const [copied, setCopied] = useState(false);

    if (!substrate) {
        return (
            <div style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "5px 16px", fontSize: "0.68rem", fontFamily: "var(--font-mono)",
                background: "rgba(245,158,11,0.06)", borderBottom: "1px solid rgba(245,158,11,0.2)",
                color: "var(--status-warn)", letterSpacing: "0.06em",
            }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--status-warn)", display: "inline-block", animation: "pulse 1.5s infinite" }} />
                SUBSTRATE IDENTITY — INITIALISING…
            </div>
        );
    }

    const hash = substrate.identity_proof?.substrate_identity_hash ?? "";
    const depth = substrate.chain_depth;
    const verified = substrate.verified && !!hash;

    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            padding: "5px 16px", fontSize: "0.67rem", fontFamily: "var(--font-mono)",
            background: verified ? "rgba(34,197,94,0.04)" : "rgba(245,158,11,0.06)",
            borderBottom: `1px solid ${verified ? "rgba(34,197,94,0.18)" : "rgba(245,158,11,0.2)"}`,
            letterSpacing: "0.06em",
        }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: verified ? "var(--status-ok)" : "var(--status-warn)", fontWeight: 700 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: verified ? "var(--status-ok)" : "var(--status-warn)", display: "inline-block" }} />
                {verified ? "IDENTITY VERIFIED" : "IDENTITY UNVERIFIED"}
            </span>
            {hash && (
                <button onClick={() => { copyToClipboard(hash); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", fontFamily: "var(--font-mono)", fontSize: "0.67rem", padding: 0 }}
                    title="Copy full identity hash">
                    HASH: {shortHash(hash)} {copied ? "?" : "?"}
                </button>
            )}
            <span style={{ color: "var(--text-tertiary)" }}>CHAIN: <span style={{ color: "var(--text-secondary)" }}>{depth}</span></span>
            <span style={{ color: "var(--text-tertiary)" }}>KERNEL: <span style={{ color: "var(--text-secondary)" }}>{substrate.kernel_version}</span></span>
            <span style={{ color: "var(--text-tertiary)" }}>LOCKFILE: <span style={{ color: "var(--text-secondary)" }}>{substrate.lockfile_version}</span></span>
            <span style={{ marginLeft: "auto", color: "var(--text-tertiary)" }}>PIEA AGI · AIEP GB2519711.2</span>
        </div>
    );
}

// -- Uncertainty bar (P105) ----------------------------------------------------

function UncertaintyBar({ value, warn, danger }: { value: number; warn: number; danger: number }) {
    const color = value >= danger ? "var(--status-error)" : value >= warn ? "var(--status-warn)" : "var(--status-ok)";
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
            {[0.15, 0.35, 0.55, 0.75, 0.95].map((thresh, i) => (
                <span key={i} style={{ width: 6, height: 9, borderRadius: 2, background: value >= thresh ? color : "var(--bg-overlay)", border: "1px solid var(--border-subtle)" }} />
            ))}
        </span>
    );
}

// -- Proof Record (P136 + P105) ------------------------------------------------

function ProofRecord({ verdict, duration_ms, ai_powered, chain_depth }: {
    verdict: SyncDeliberationResult["verdict"];
    duration_ms?: number;
    ai_powered?: boolean;
    chain_depth?: number;
}) {
    const [copied, setCopied] = useState<string | null>(null);
    const ur = verdict.uncertainty_record;

    const copy = (text: string, key: string) => { copyToClipboard(text); setCopied(key); setTimeout(() => setCopied(null), 2000); };

    const verdictColor = verdict.verdict_type === "ConsensusVerdict" ? "var(--status-ok)"
        : verdict.verdict_type === "EscalationRecord" ? "var(--status-warn)" : "var(--text-tertiary)";

    return (
        <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.67rem", letterSpacing: "0.05em", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", background: "var(--bg-overlay)", marginBottom: 10, overflow: "hidden" }}>
            {/* Row 1 */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "6px 12px", borderBottom: ur ? "1px solid var(--border-subtle)" : undefined }}>
                <span style={{ fontWeight: 700, color: verdictColor, textTransform: "uppercase", letterSpacing: "0.08em" }}>{verdict.verdict_type}</span>
                <button onClick={() => copy(verdict.verdict_hash, "hash")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", fontFamily: "var(--font-mono)", fontSize: "0.67rem", padding: 0 }} title="Copy verdict hash">
                    VERDICT: {shortHash(verdict.verdict_hash)} {copied === "hash" ? "?" : "?"}
                </button>
                {chain_depth !== undefined && <span style={{ color: "var(--text-tertiary)" }}>CHAIN: <span style={{ color: "var(--text-secondary)" }}>{chain_depth}</span></span>}
                {duration_ms !== undefined && <span style={{ color: "var(--text-tertiary)" }}>{duration_ms}ms</span>}
                {ai_powered && <span style={{ color: "var(--accent-verified)", fontWeight: 600 }}>? AI</span>}
                <span style={{ marginLeft: "auto", color: "var(--text-tertiary)" }}>{Math.round(verdict.verified_confidence * 100)}% · dissent L{verdict.dissent_level}</span>
            </div>
            {/* Row 2: P105 Uncertainty */}
            {ur && (
                <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "5px 12px", borderBottom: verdict.what_would_confirm ? "1px solid var(--border-subtle)" : undefined }}>
                    <span style={{ color: "var(--text-tertiary)" }}>P105</span>
                    <span title="EvidenceContestationScore">
                        <span style={{ color: "var(--text-tertiary)" }}>CONTESTED </span>
                        <UncertaintyBar value={ur.evidence_contestation_score} warn={0.4} danger={0.65} />
                        <span style={{ color: "var(--text-secondary)", marginLeft: 4 }}>{(ur.evidence_contestation_score * 100).toFixed(0)}%</span>
                    </span>
                    <span title="BranchDivergenceScore">
                        <span style={{ color: "var(--text-tertiary)" }}>DIVERGE </span>
                        <UncertaintyBar value={ur.branch_divergence_score} warn={0.35} danger={0.6} />
                        <span style={{ color: "var(--text-secondary)", marginLeft: 4 }}>{(ur.branch_divergence_score * 100).toFixed(0)}%</span>
                    </span>
                    {ur.substrate_boundary_flag && <span style={{ color: "var(--status-warn)", fontWeight: 700 }}>? BOUNDARY</span>}
                    <button onClick={() => copy(ur.uncertainty_hash, "uhash")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: "0.67rem", padding: 0, marginLeft: "auto" }} title="Copy uncertainty hash">
                        U-HASH: {shortHash(ur.uncertainty_hash)} {copied === "uhash" ? "?" : "?"}
                    </button>
                </div>
            )}
            {/* Row 3: Resolution path */}
            {verdict.what_would_confirm && (
                <div style={{ padding: "5px 12px", color: "var(--text-tertiary)", fontFamily: "var(--font-sans)", fontSize: "0.72rem", lineHeight: 1.4 }}>
                    <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600, fontSize: "0.63rem", color: "var(--text-tertiary)", letterSpacing: "0.08em", marginRight: 8 }}>RESOLUTION PATH</span>
                    {verdict.what_would_confirm}
                </div>
            )}
        </div>
    );
}

// -- Three-channel display -----------------------------------------------------

function ChannelDisplay({ verdict }: { verdict: SyncDeliberationResult["verdict"] }) {
    if (!verdict || verdict.instinct_basis === "SCOPE_GATE") {
        return <div style={{ padding: "10px 14px", borderRadius: "var(--radius-md)", background: "var(--bg-overlay)", border: "1px solid var(--border-subtle)", fontSize: "0.88rem", color: "var(--text-secondary)" }}>{verdict?.verified_answer}</div>;
    }

    const mdStyle = { fontSize: "0.9rem", lineHeight: 1.7 };
    const mdComponents = {
        p: ({ children }: { children: React.ReactNode }) => <p style={{ marginBottom: "0.6em" }}>{children}</p>,
        ul: ({ children }: { children: React.ReactNode }) => <ul style={{ paddingLeft: "1.4em", marginBottom: "0.6em" }}>{children}</ul>,
        ol: ({ children }: { children: React.ReactNode }) => <ol style={{ paddingLeft: "1.4em", marginBottom: "0.6em" }}>{children}</ol>,
        li: ({ children }: { children: React.ReactNode }) => <li style={{ marginBottom: "0.2em" }}>{children}</li>,
        h1: ({ children }: { children: React.ReactNode }) => <h1 style={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: "0.4em" }}>{children}</h1>,
        h2: ({ children }: { children: React.ReactNode }) => <h2 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "0.4em" }}>{children}</h2>,
        h3: ({ children }: { children: React.ReactNode }) => <h3 style={{ fontSize: "0.95rem", fontWeight: 600, marginBottom: "0.3em" }}>{children}</h3>,
        strong: ({ children }: { children: React.ReactNode }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
        code: ({ children }: { children: React.ReactNode }) => <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.85em", background: "var(--bg-overlay)", padding: "1px 5px", borderRadius: 4 }}>{children}</code>,
        blockquote: ({ children }: { children: React.ReactNode }) => <blockquote style={{ borderLeft: "3px solid var(--accent-verified)", paddingLeft: "0.8em", marginLeft: 0, color: "var(--text-secondary)", fontStyle: "italic" }}>{children}</blockquote>,
        a: ({ href, children }: { href?: string; children: React.ReactNode }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent-verified)", textDecoration: "underline" }}>{children}</a>,
    } as Record<string, unknown>;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div className="channel channel--verified">
                <div className="ch-label">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M2 5l2.5 2.5L8 3" /></svg>
                    VERIFIED CHANNEL
                </div>
                <div className="ch-content" style={mdStyle}>
                    {verdict.verified_answer
                        ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{verdict.verified_answer}</ReactMarkdown>
                        : <em style={{ color: "var(--text-tertiary)" }}>Insufficient evidence for verified answer.</em>
                    }
                </div>
                <div style={{ marginTop: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <div className="progress" style={{ flex: 1, height: 5 }}>
                            <div className="progress-bar" style={{ width: `${Math.round(verdict.verified_confidence * 100)}%`, background: "var(--accent-verified)", height: "100%" }} />
                        </div>
                        <span className="ch-confidence">{Math.round(verdict.verified_confidence * 100)}%</span>
                    </div>
                    {verdict.verified_sources.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {verdict.verified_sources.slice(0, 6).map((s, i) => (
                                <a key={i} href={s.source_url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 8px", borderRadius: 10, background: "var(--bg-overlay)", border: "1px solid var(--border-subtle)", color: "var(--accent-verified)", textDecoration: "none", fontSize: "0.68rem", fontFamily: "var(--font-mono)" }}>
                                    ↗ {hostname(s.source_url)}
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {verdict.theory_content && (
                <div className="channel channel--theory">
                    <div className="ch-label">
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M5 1v4l2.5 2.5" /><circle cx="5" cy="5" r="4" /></svg>
                        THEORY CHANNEL — UNVERIFIED HYPOTHESIS
                    </div>
                    <div className="ch-content" style={mdStyle}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{verdict.theory_content}</ReactMarkdown>
                    </div>
                    {verdict.escalation_reason && (
                        <div style={{ marginTop: 8, fontSize: "0.72rem", color: "var(--status-warn)", fontFamily: "var(--font-mono)" }}>⚠ ESCALATION: {verdict.escalation_reason}</div>
                    )}
                </div>
            )}
            {verdict.instinct_content && (
                <div className="channel channel--instinct">
                    <div className="ch-label">
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="5" cy="5" r="3" /><circle cx="5" cy="5" r="1" fill="currentColor" stroke="none" /></svg>
                        INSTINCT SIGNAL — PRE-EVIDENTIAL PATTERN
                    </div>
                    <div className="ch-content" style={{ ...mdStyle, fontStyle: "italic" }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{verdict.instinct_content}</ReactMarkdown>
                    </div>
                    <div style={{ marginTop: 6, fontSize: "0.65rem", color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                        BASIS: {verdict.instinct_basis}{verdict.dissent_records > 0 && ` · ${verdict.dissent_records} dissent record${verdict.dissent_records !== 1 ? "s" : ""}`}
                    </div>
                </div>
            )}
        </div>
    );
}

// -- Typewriter ---------------------------------------------------------------

function TypewriterText({ text, components }: { text: string; components: Record<string, unknown> }) {
    const [displayed, setDisplayed] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!text) { setDisplayed(""); setDone(true); return; }
        setDisplayed("");
        setDone(false);
        let i = 0;
        const id = setInterval(() => {
            i += 3; // 3 chars per tick = ~250 chars/sec at 12ms
            setDisplayed(text.slice(0, i));
            if (i >= text.length) { clearInterval(id); setDone(true); }
        }, 12);
        return () => clearInterval(id);
    }, [text]);

    return (
        <span>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{displayed}</ReactMarkdown>
            {!done && <span className="tw-cursor">▍</span>}
        </span>
    );
}

// -- Message bubble -----------------------------------------------------------

function MessageBubble({ msg, isLatest }: { msg: Message; isLatest?: boolean }) {
    const [analysisOpen, setAnalysisOpen] = useState(false);

    const mdComponents = {
        p: ({ children }: { children: React.ReactNode }) => <p style={{ marginBottom: "0.6em" }}>{children}</p>,
        ul: ({ children }: { children: React.ReactNode }) => <ul style={{ paddingLeft: "1.4em", marginBottom: "0.6em" }}>{children}</ul>,
        ol: ({ children }: { children: React.ReactNode }) => <ol style={{ paddingLeft: "1.4em", marginBottom: "0.6em" }}>{children}</ol>,
        li: ({ children }: { children: React.ReactNode }) => <li style={{ marginBottom: "0.2em" }}>{children}</li>,
        h2: ({ children }: { children: React.ReactNode }) => <h2 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "0.4em" }}>{children}</h2>,
        h3: ({ children }: { children: React.ReactNode }) => <h3 style={{ fontSize: "0.95rem", fontWeight: 600, marginBottom: "0.3em" }}>{children}</h3>,
        strong: ({ children }: { children: React.ReactNode }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
        code: ({ children }: { children: React.ReactNode }) => <code style={{ fontFamily: "var(--font-mono)", fontSize: "0.85em", background: "var(--bg-overlay)", padding: "1px 5px", borderRadius: 4 }}>{children}</code>,
        blockquote: ({ children }: { children: React.ReactNode }) => <blockquote style={{ borderLeft: "3px solid var(--accent-verified)", paddingLeft: "0.8em", marginLeft: 0, color: "var(--text-secondary)", fontStyle: "italic" }}>{children}</blockquote>,
        a: ({ href, children }: { href?: string; children: React.ReactNode }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent-verified)", textDecoration: "underline" }}>{children}</a>,
    } as Record<string, unknown>;

    if (msg.role === "user") {
        return (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
                <div style={{ maxWidth: "min(600px, 84vw)", background: "var(--brand)", color: "#fff", borderRadius: "18px 18px 4px 18px", padding: "10px 16px", fontSize: "0.92rem", lineHeight: 1.65 }}>
                    {msg.content}
                </div>
            </div>
        );
    }

    if (msg.streaming) {
        return (
            <div style={{ marginBottom: 24, display: "flex", gap: 10, alignItems: "flex-start" }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--brand-subtle)", border: "1px solid var(--border-brand)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round"><circle cx="6" cy="6" r="3" /><circle cx="6" cy="6" r="5.5" strokeDasharray="2 2" strokeOpacity=".4" /></svg>
                </div>
                <div style={{ paddingTop: 4, fontSize: "0.88rem", color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", display: "flex", gap: 5, alignItems: "center" }}>
                    <span style={{ animation: "pulse 1.2s infinite" }}>●</span>
                    <span style={{ animation: "pulse 1.2s 0.3s infinite" }}>●</span>
                    <span style={{ animation: "pulse 1.2s 0.6s infinite" }}>●</span>
                </div>
            </div>
        );
    }

    const verdict = msg.verdict;
    const answer = verdict?.verified_answer ?? msg.content;

    return (
        <div style={{ marginBottom: 28, display: "flex", gap: 10, alignItems: "flex-start" }}>
            {/* Avatar */}
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--brand-subtle)", border: "1px solid var(--border-brand)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round"><circle cx="6" cy="6" r="3" /><circle cx="6" cy="6" r="5.5" strokeDasharray="2 2" strokeOpacity=".4" /></svg>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
                {/* Main answer — typewriter on the latest message, plain on history */}
                <div style={{ fontSize: "0.92rem", lineHeight: 1.75, color: "var(--text-primary)", marginBottom: 12 }}>
                    {isLatest && answer
                        ? <TypewriterText text={answer} components={mdComponents} />
                        : <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{answer ?? ""}</ReactMarkdown>
                    }
                </div>

                {/* Sources */}
                {verdict && verdict.verified_sources.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
                        {verdict.verified_sources.slice(0, 6).map((s, i) => (
                            <a key={i} href={s.source_url} target="_blank" rel="noopener noreferrer"
                                style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 8px", borderRadius: 10, background: "var(--bg-overlay)", border: "1px solid var(--border-subtle)", color: "var(--accent-verified)", textDecoration: "none", fontSize: "0.68rem", fontFamily: "var(--font-mono)" }}>
                                ↗ {hostname(s.source_url)}
                            </a>
                        ))}
                    </div>
                )}

                {/* Compact proof + analysis toggle */}
                {verdict && (
                    <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: "0.65rem", fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", flexWrap: "wrap" }}>
                        <span style={{ color: verdict.verdict_type === "ConsensusVerdict" ? "var(--status-ok)" : "var(--status-warn)", fontWeight: 700 }}>{verdict.verdict_type}</span>
                        <span>{Math.round(verdict.verified_confidence * 100)}%</span>
                        {msg.duration_ms && <span>{msg.duration_ms}ms</span>}
                        {msg.ai_powered && <span style={{ color: "var(--accent-verified)" }}>● AI</span>}
                        <button onClick={() => setAnalysisOpen(o => !o)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: "0.65rem", padding: 0 }}>
                            {analysisOpen ? "▾ Hide analysis" : "▸ Full analysis"}
                        </button>
                        <button onClick={() => copyToClipboard(verdict.verdict_hash)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", fontSize: "0.65rem", padding: 0 }}
                            title={verdict.verdict_hash}>
                            ⎘ {shortHash(verdict.verdict_hash)}
                        </button>
                    </div>
                )}

                {/* Expandable analysis: theory, instinct, uncertainty */}
                {verdict && analysisOpen && (
                    <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                        {verdict.theory_content && (
                            <div className="channel channel--theory">
                                <div className="ch-label">
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M5 1v4l2.5 2.5" /><circle cx="5" cy="5" r="4" /></svg>
                                    THEORY — UNVERIFIED HYPOTHESIS
                                </div>
                                <div className="ch-content" style={{ fontSize: "0.88rem", lineHeight: 1.65 }}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{verdict.theory_content}</ReactMarkdown>
                                </div>
                            </div>
                        )}
                        {verdict.instinct_content && (
                            <div className="channel channel--instinct">
                                <div className="ch-label">
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="5" cy="5" r="3" /><circle cx="5" cy="5" r="1" fill="currentColor" stroke="none" /></svg>
                                    INSTINCT SIGNAL
                                </div>
                                <div className="ch-content" style={{ fontSize: "0.88rem", lineHeight: 1.65, fontStyle: "italic" }}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{verdict.instinct_content}</ReactMarkdown>
                                </div>
                            </div>
                        )}
                        {verdict.uncertainty_record && (
                            <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.65rem", color: "var(--text-tertiary)", display: "flex", gap: 14, flexWrap: "wrap", padding: "6px 10px", background: "var(--bg-overlay)", borderRadius: "var(--radius-sm)" }}>
                                <span>CONTESTED {(verdict.uncertainty_record.evidence_contestation_score * 100).toFixed(0)}%</span>
                                <span>DIVERGE {(verdict.uncertainty_record.branch_divergence_score * 100).toFixed(0)}%</span>
                                {verdict.uncertainty_record.substrate_boundary_flag && <span style={{ color: "var(--status-warn)" }}>⚠ BOUNDARY</span>}
                                <span title={verdict.uncertainty_record.uncertainty_hash}>U-HASH: {shortHash(verdict.uncertainty_record.uncertainty_hash)}</span>
                            </div>
                        )}
                    </div>
                )}

                <div style={{ marginTop: 8, fontSize: "0.63rem", color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                    {timeAgo(msg.timestamp)}{msg.depth && ` · ${msg.depth}`}
                </div>
            </div>
        </div>
    );
}

// -- Utility components --------------------------------------------------------

function SectionTitle({ children }: { children: ReactNode }) {
    return <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--text-primary)", marginBottom: 14 }}>{children}</div>;
}
function Muted({ children }: { children: ReactNode }) {
    return <div style={{ color: "var(--text-tertiary)", fontSize: "0.82rem" }}>{children}</div>;
}
function SignalGroup({ label, color, children }: { label: string; color: string; children: ReactNode }) {
    return (
        <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: "0.63rem", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", color, marginBottom: 8, fontWeight: 700 }}>{label}</div>
            {children}
        </div>
    );
}
function SignalCard({ title, sub }: { title: string; sub?: string }) {
    return (
        <div style={{ padding: "8px 12px", borderRadius: 8, background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", marginBottom: 6 }}>
            <div style={{ fontSize: "0.82rem", color: "var(--text-primary)", fontWeight: 500 }}>{title}</div>
            {sub && <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary)", marginTop: 3, fontFamily: "var(--font-mono)" }}>{sub}</div>}
        </div>
    );
}

// -- Signals panel -------------------------------------------------------------

function SignalsPanel() {
    const [feed, setFeed] = useState<SignalsFeed | null>(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => { getSignals().then(setFeed).catch(() => null).finally(() => setLoading(false)); }, []);
    const total = feed ? feed.curiosity_gaps.length + feed.expiring_predictions.length + feed.pending_proposals.length + feed.active_sessions.length : 0;
    return (
        <div style={{ padding: 16 }}>
            <SectionTitle>Substrate Signals</SectionTitle>
            {loading && <Muted>Loading…</Muted>}
            {feed?.active_sessions.length > 0 && (
                <SignalGroup label="ACTIVE SESSIONS" color="var(--accent-verified)">
                    {feed.active_sessions.map((s) => <SignalCard key={s.session_id} title={`⟳ ${s.trigger_mode} · ${s.cycles_completed}/${s.max_cycles} cycles`} sub={s.current_focus?.slice(0, 100)} />)}
                </SignalGroup>
            )}
            {feed?.curiosity_gaps.length > 0 && (
                <SignalGroup label="CURIOSITY GAPS" color="var(--accent-theory)">
                    {feed.curiosity_gaps.map((g) => <SignalCard key={g.curiosity_id} title={g.question_generated.slice(0, 120)} sub={`${g.source_domain} · priority ${(g.priority_score * 100).toFixed(0)}%`} />)}
                </SignalGroup>
            )}
            {feed?.expiring_predictions.length > 0 && (
                <SignalGroup label="EXPIRING PREDICTIONS" color="var(--status-warn)">
                    {feed.expiring_predictions.map((p) => <SignalCard key={p.prediction_id} title={p.claim.slice(0, 120)} sub={`Due ${new Date(p.time_horizon).toLocaleDateString()} · ${(p.confidence * 100).toFixed(0)}%`} />)}
                </SignalGroup>
            )}
            {feed?.pending_proposals.length > 0 && (
                <SignalGroup label="PENDING GOVERNANCE APPROVAL" color="var(--status-warn)">
                    {feed.pending_proposals.map((p) => <SignalCard key={p.record_id} title={`${p.proposal_type}: ${p.description.slice(0, 100)}`} />)}
                </SignalGroup>
            )}
            {!loading && total === 0 && <Muted>No pending signals. Substrate monitoring active.</Muted>}
        </div>
    );
}

// -- Goals panel ---------------------------------------------------------------

function GoalsPanel() {
    const [goals, setGoals] = useState<Goal[]>([]);
    const [loading, setLoading] = useState(true);
    const [title, setTitle] = useState(""); const [desc, setDesc] = useState(""); const [type, setType] = useState("RESEARCH"); const [adding, setAdding] = useState(false);
    useEffect(() => { getGoals().then((r) => setGoals(r.goals)).catch(() => null).finally(() => setLoading(false)); }, []);
    async function addGoal() {
        if (!title.trim()) return; setAdding(true);
        try { const g = await createGoal({ title: title.trim(), description: desc.trim(), goal_type: type }); setGoals((prev) => [g, ...prev]); setTitle(""); setDesc(""); }
        catch { /* ignore */ } finally { setAdding(false); }
    }
    return (
        <div style={{ padding: 16 }}>
            <SectionTitle>Goal Registry</SectionTitle>
            <div style={{ marginBottom: 14, padding: 12, borderRadius: 10, background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Goal title…" style={{ width: "100%", background: "transparent", border: "none", borderBottom: "1px solid var(--border-subtle)", color: "var(--text-primary)", fontSize: "0.88rem", padding: "4px 0", outline: "none", marginBottom: 8 }} />
                <textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (optional)" rows={2} style={{ width: "100%", background: "transparent", border: "none", borderBottom: "1px solid var(--border-subtle)", color: "var(--text-primary)", fontSize: "0.82rem", padding: "4px 0", outline: "none", resize: "none", marginBottom: 8, fontFamily: "inherit" }} />
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select value={type} onChange={(e) => setType(e.target.value)} style={{ background: "var(--bg-overlay)", border: "1px solid var(--border-subtle)", color: "var(--text-secondary)", borderRadius: 6, padding: "3px 8px", fontSize: "0.78rem" }}>
                        <option value="RESEARCH">Research</option><option value="MONITOR">Monitor</option><option value="ANALYSIS">Analysis</option><option value="PREDICTION">Prediction</option>
                    </select>
                    <button onClick={addGoal} disabled={adding || !title.trim()} style={{ marginLeft: "auto", padding: "4px 14px", borderRadius: 6, background: "var(--brand)", color: "#fff", border: "none", cursor: "pointer", fontSize: "0.82rem", opacity: adding ? 0.6 : 1 }}>{adding ? "…" : "+ Add"}</button>
                </div>
            </div>
            {loading && <Muted>Loading…</Muted>}
            {!loading && goals.length === 0 && <Muted>No goals yet.</Muted>}
            {goals.map((g) => (
                <div key={g.goal_id} style={{ padding: "10px 12px", borderRadius: 8, background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: "0.63rem", fontFamily: "var(--font-mono)", color: g.status === "ACTIVE" ? "var(--accent-verified)" : "var(--text-tertiary)", background: "var(--bg-overlay)", padding: "1px 6px", borderRadius: 10 }}>{g.goal_type}</span>
                        <span style={{ fontWeight: 600, fontSize: "0.88rem", color: "var(--text-primary)" }}>{g.title}</span>
                    </div>
                    {g.description && <div style={{ color: "var(--text-secondary)", fontSize: "0.8rem", marginTop: 4 }}>{g.description.slice(0, 120)}</div>}
                    <div style={{ color: "var(--text-tertiary)", fontSize: "0.68rem", marginTop: 4, fontFamily: "var(--font-mono)" }}>{Math.round(g.completion_score * 100)}% complete · priority {g.priority} · {g.status}</div>
                </div>
            ))}
        </div>
    );
}

// -- Projects panel ------------------------------------------------------------

function ProjectsPanel({ onSelect }: { onSelect: (p: Project) => void }) {
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState(""); const [adding, setAdding] = useState(false);
    useEffect(() => { getProjects().then((r) => setProjects(r.projects)).catch(() => null).finally(() => setLoading(false)); }, []);
    async function addProject() {
        if (!name.trim()) return; setAdding(true);
        try { const p = await createProject({ name: name.trim() }); setProjects((prev) => [p, ...prev]); setName(""); }
        catch { /* ignore */ } finally { setAdding(false); }
    }
    return (
        <div style={{ padding: 16 }}>
            <SectionTitle>Projects</SectionTitle>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New project name…" onKeyDown={(e) => e.key === "Enter" && addProject()} style={{ flex: 1, background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", borderRadius: 8, color: "var(--text-primary)", fontSize: "0.88rem", padding: "8px 12px", outline: "none" }} />
                <button onClick={addProject} disabled={adding || !name.trim()} style={{ padding: "8px 14px", borderRadius: 8, background: "var(--brand)", color: "#fff", border: "none", cursor: "pointer", fontSize: "0.85rem", opacity: adding ? 0.6 : 1 }}>{adding ? "…" : "+"}</button>
            </div>
            {loading && <Muted>Loading…</Muted>}
            {!loading && projects.length === 0 && <Muted>No projects yet.</Muted>}
            {projects.map((p) => (
                <div key={p.project_id} onClick={() => onSelect(p)} style={{ padding: "10px 12px", borderRadius: 8, background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", marginBottom: 8, cursor: "pointer" }}>
                    <div style={{ fontWeight: 600, fontSize: "0.88rem", color: "var(--text-primary)" }}>{p.name}</div>
                    {p.description && <div style={{ color: "var(--text-secondary)", fontSize: "0.8rem", marginTop: 2 }}>{p.description.slice(0, 80)}</div>}
                    <div style={{ color: "var(--text-tertiary)", fontSize: "0.68rem", marginTop: 4, fontFamily: "var(--font-mono)" }}>{timeAgo(p.last_active_at)}</div>
                </div>
            ))}
        </div>
    );
}

// -- History panel -------------------------------------------------------------

function HistoryPanel({ onLoad }: { onLoad: (question: string, depth: Depth) => void }) {
    const [records, setRecords] = useState<DeliberationRecord[]>([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => { getDeliberationHistory(50).then((r) => setRecords(r.deliberations)).catch(() => null).finally(() => setLoading(false)); }, []);
    return (
        <div style={{ padding: 16 }}>
            <SectionTitle>Deliberation History</SectionTitle>
            <div style={{ marginBottom: 14 }}><Muted>Last 50 deliberations — immutable ledger</Muted></div>
            {loading && <Muted>Loading…</Muted>}
            {!loading && records.length === 0 && <Muted>No history. Start a deliberation.</Muted>}
            {records.map((r) => {
                const content = (() => { try { return JSON.parse(r.verdict_content ?? "{}") as { verified_answer?: string }; } catch { return {}; } })();
                const preview = content.verified_answer?.slice(0, 140) ?? "—";
                const depth: Depth = r.query_class === "DEEP_RESEARCH" ? "DEEP_RESEARCH" : r.query_class === "ANALYTICAL" ? "ANALYTICAL" : "CONVERSATIONAL";
                return (
                    <div key={r.deliberation_id} onClick={() => onLoad(preview, depth)} style={{ padding: "10px 12px", borderRadius: 8, background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", marginBottom: 8, cursor: "pointer" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <span style={{ fontSize: "0.63rem", fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", background: "var(--bg-overlay)", padding: "1px 6px", borderRadius: 10 }}>{r.query_class ?? "CONVERSATIONAL"}</span>
                            {r.agreement_rate != null && <span style={{ fontSize: "0.63rem", fontFamily: "var(--font-mono)", color: r.agreement_rate >= 0.75 ? "var(--status-ok)" : r.agreement_rate >= 0.5 ? "var(--status-warn)" : "var(--status-error)" }}>{Math.round(r.agreement_rate * 100)}%</span>}
                            <span style={{ marginLeft: "auto", fontSize: "0.63rem", color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>{timeAgo(r.created_at)}</span>
                        </div>
                        <div style={{ color: "var(--text-secondary)", fontSize: "0.82rem", lineHeight: 1.5 }}>{preview}{preview.length >= 140 ? "…" : ""}</div>
                        {r.evidence_count > 0 && <div style={{ marginTop: 4, fontSize: "0.63rem", color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>{r.evidence_count} sources · {r.hypothesis_count} hypotheses</div>}
                    </div>
                );
            })}
        </div>
    );
}

// -- Depth selector ------------------------------------------------------------

const DEPTH_INFO: Record<Depth, { label: string; desc: string; color: string }> = {
    CONVERSATIONAL: { label: "Quick", desc: "Fast heuristic · immediate answer", color: "var(--accent-instinct)" },
    ANALYTICAL: { label: "Analytical", desc: "Full 3-channel · deterministic pipeline · evidence grounded", color: "var(--accent-verified)" },
    DEEP_RESEARCH: { label: "Deep", desc: "Multi-domain · higher confidence threshold · extended reasoning", color: "var(--brand)" },
};

function DepthSelector({ value, onChange }: { value: Depth; onChange: (d: Depth) => void }) {
    return (
        <div style={{ display: "flex", background: "var(--bg-overlay)", borderRadius: 8, padding: 2, border: "1px solid var(--border-subtle)", gap: 2 }}>
            {(["CONVERSATIONAL", "ANALYTICAL", "DEEP_RESEARCH"] as Depth[]).map((d) => (
                <button key={d} onClick={() => onChange(d)} title={DEPTH_INFO[d].desc} style={{ padding: "3px 10px", borderRadius: 6, border: "none", cursor: "pointer", background: value === d ? DEPTH_INFO[d].color : "transparent", color: value === d ? "#fff" : "var(--text-secondary)", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
                    {DEPTH_INFO[d].label}
                </button>
            ))}
        </div>
    );
}

// -- Empty state ---------------------------------------------------------------

function StatusRow({ label, value, ok, warn }: { label: string; value: string; ok?: boolean; warn?: boolean }) {
    const color = ok ? "var(--status-ok)" : warn ? "var(--status-warn)" : "var(--text-secondary)";
    return (
        <>
            <span style={{ fontSize: "0.72rem", color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>{label}</span>
            <span style={{ fontSize: "0.72rem", color, fontFamily: "var(--font-mono)", fontWeight: 600 }}>{value}</span>
        </>
    );
}

function EmptyState({ substrate, depth, signalCount }: { substrate: SubstrateState | null; depth: Depth; signalCount: number }) {
    return (
        <div style={{ margin: "auto", padding: "32px 20px", maxWidth: 540 }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>
                <span style={{ color: "var(--brand)" }}>PIEA</span> AGI
            </div>
            <div style={{ color: "var(--text-tertiary)", fontSize: "0.72rem", fontFamily: "var(--font-mono)", marginBottom: 20, letterSpacing: "0.06em" }}>
                GOVERNED REASONING SUBSTRATE · AIEP GB2519711.2
            </div>
            <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", overflow: "hidden", marginBottom: 20 }}>
                <div style={{ padding: "7px 14px", background: "var(--bg-surface)", borderBottom: "1px solid var(--border-subtle)", fontSize: "0.63rem", fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", letterSpacing: "0.09em", fontWeight: 700 }}>SUBSTRATE STATUS</div>
                <div style={{ padding: "12px 14px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 20px" }}>
                    <StatusRow label="Identity" value={substrate?.verified ? "VERIFIED" : substrate ? "UNVERIFIED" : "INITIALISING…"} ok={!!substrate?.verified} warn={!!substrate && !substrate.verified} />
                    <StatusRow label="Chain depth" value={substrate ? String(substrate.chain_depth) : "—"} />
                    <StatusRow label="Kernel" value={substrate?.kernel_version ?? "—"} />
                    <StatusRow label="Pending signals" value={String(signalCount)} ok={signalCount === 0} warn={signalCount > 0} />
                </div>
            </div>
            <div style={{ color: "var(--text-secondary)", fontSize: "0.85rem", lineHeight: 1.7, marginBottom: 20 }}>
                Every deliberation produces a cryptographically committed{" "}
                <span style={{ color: "var(--accent-verified)", fontFamily: "var(--font-mono)", fontSize: "0.78rem" }}>ThreeChannelVerdict</span>{" "}
                bound to the immutable Reasoning Ledger. Hashes are independently auditable.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {(["ANALYTICAL", "DEEP_RESEARCH", "CONVERSATIONAL"] as Depth[]).map((d) => (
                    <div key={d} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, background: "var(--bg-surface)", border: `1px solid ${d === depth ? "var(--border-brand)" : "var(--border-subtle)"}` }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: DEPTH_INFO[d].color, flexShrink: 0 }} />
                        <div>
                            <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--text-primary)" }}>{DEPTH_INFO[d].label}</div>
                            <div style={{ fontSize: "0.72rem", color: "var(--text-tertiary)" }}>{DEPTH_INFO[d].desc}</div>
                        </div>
                        {d === depth && <span style={{ marginLeft: "auto", fontSize: "0.63rem", color: "var(--brand)", fontFamily: "var(--font-mono)", fontWeight: 700 }}>SELECTED</span>}
                    </div>
                ))}
            </div>
        </div>
    );
}

// -- Main App ------------------------------------------------------------------

export default function App() {
    const [apiKey, setApiKeyState] = useState<string>(() => getApiKey());
    const [view, setView] = useState<View>("chat");
    const [depth, setDepth] = useState<Depth>("ANALYTICAL");
    const [agiMode, setAgiModeState] = useState<AgiMode>(() => getAgiMode());
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [activeProject, setActiveProject] = useState<Project | null>(null);
    const [signalCount, setSignalCount] = useState(0);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem("piea-agi-dark") !== "0");
    const [substrate, setSubstrate] = useState<SubstrateState | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
    useEffect(() => { document.documentElement.setAttribute("data-theme", darkMode ? "midnight" : "light"); localStorage.setItem("piea-agi-dark", darkMode ? "1" : "0"); }, [darkMode]);

    // P104 — Initialise substrate identity on mount
    useEffect(() => {
        if (!apiKey) return;
        (async () => {
            try {
                const [health, session] = await Promise.all([
                    getHealth(),
                    startSubstrateSession().catch(() => null),
                ]);
                setSubstrate({
                    identity_proof: health.identity_proof,
                    chain_depth: session?.reasoning_ledger_depth ?? health.identity_proof?.chain_depth ?? 0,
                    kernel_version: health.genome_kernel,
                    lockfile_version: health.lockfile_version,
                    verified: !!health.identity_proof?.verified || !!session,
                });
            } catch {
                setSubstrate({ identity_proof: null, chain_depth: 0, kernel_version: "unknown", lockfile_version: "unknown", verified: false });
            }
        })();
    }, [apiKey]);

    // Signal polling
    useEffect(() => {
        if (!apiKey) return;
        const poll = () => getSignals().then((f) => setSignalCount(f.curiosity_gaps.length + f.expiring_predictions.length + f.pending_proposals.length)).catch(() => null);
        poll();
        const id = setInterval(poll, 60_000);
        return () => clearInterval(id);
    }, [apiKey]);

    useEffect(() => { if (agiMode === "autonomous" && apiKey) triggerAutonomousCycle().catch(() => null); }, [agiMode, apiKey]);

    const handleSend = useCallback(async () => {
        const text = input.trim();
        if (!text || sending) return;
        setInput(""); setSending(true); setView("chat");

        const userMsg: Message = { id: crypto.randomUUID(), role: "user", content: text, timestamp: new Date().toISOString() };
        const thinkingMsg: Message = { id: crypto.randomUUID(), role: "agi", content: "", streaming: true, timestamp: new Date().toISOString() };
        setMessages((prev) => [...prev, userMsg, thinkingMsg]);

        try {
            const result = await deliberateSync({ question: text, project_id: activeProject?.project_id, requested_depth: depth });
            const agiMsg: Message = {
                id: result.deliberation_id, role: "agi",
                content: result.verdict.verified_answer ?? "No verified answer produced.",
                verdict: result.verdict,
                sources: result.verdict.verified_sources ?? [],
                confidence: result.verdict.verified_confidence,
                ai_powered: result.ai_powered,
                chain_depth: result.chain_depth ?? substrate?.chain_depth,
                duration_ms: result.duration_ms,
                depth, timestamp: new Date().toISOString(),
            };
            if (result.chain_depth !== undefined) setSubstrate((prev) => prev ? { ...prev, chain_depth: result.chain_depth! } : prev);
            setMessages((prev) => [...prev.slice(0, -1), agiMsg]);
        } catch (err) {
            setMessages((prev) => [...prev.slice(0, -1), {
                id: crypto.randomUUID(), role: "agi",
                content: err instanceof Error ? `Deliberation error: ${err.message}` : "Worker did not respond. Check substrate status.",
                timestamp: new Date().toISOString(),
            }]);
        } finally {
            setSending(false);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [input, sending, depth, activeProject, substrate]);

    if (!apiKey) {
        return <Landing onSignIn={() => { const k = getApiKey(); if (k) setApiKeyState(k); }} theme={darkMode ? "dark" : "light"} onToggleTheme={() => setDarkMode(d => !d)} />;
    }

    const navBtn = (v: View, label: ReactNode, badge?: number) => (
        <button onClick={() => { setView(v); setSidebarOpen(false); }} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 12px", borderRadius: 8, background: view === v ? "var(--brand-subtle)" : "transparent", border: view === v ? "1px solid var(--border-brand)" : "1px solid transparent", color: view === v ? "var(--brand)" : "var(--text-secondary)", cursor: "pointer", fontSize: "0.85rem", fontWeight: view === v ? 600 : 400 }}>
            {label}
            {badge ? <span style={{ marginLeft: "auto", background: "var(--status-warn)", color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: "0.63rem", fontWeight: 700 }}>{badge}</span> : null}
        </button>
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", minHeight: "100dvh", background: "var(--bg-base)" }}>
            {/* P104 Identity Rail */}
            <IdentityRail substrate={substrate} />

            {/* Header */}
            <header style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 14px", height: 48, background: "var(--bg-surface)", borderBottom: "1px solid var(--border-subtle)", position: "sticky", top: 0, zIndex: 100, flexShrink: 0 }}>
                <button onClick={() => setSidebarOpen(o => !o)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", padding: 6, borderRadius: 6, display: "flex" }}>
                    <svg width="17" height="17" viewBox="0 0 16 16" fill="currentColor"><path d="M1 2.75A.75.75 0 011.75 2h12.5a.75.75 0 010 1.5H1.75A.75.75 0 011 2.75zm0 5A.75.75 0 011.75 7h12.5a.75.75 0 010 1.5H1.75A.75.75 0 011 7.75zM1.75 12h12.5a.75.75 0 010 1.5H1.75a.75.75 0 010-1.5z" /></svg>
                </button>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.92rem", letterSpacing: "0.04em", color: "var(--text-primary)", userSelect: "none" }}>
                    <span style={{ color: "var(--brand)" }}>PIEA</span> AGI
                </div>
                <div style={{ marginLeft: 12 }}><DepthSelector value={depth} onChange={setDepth} /></div>
                <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
                    <button onClick={() => { const next: AgiMode = agiMode === "manual" ? "autonomous" : "manual"; setAgiMode(next); setAgiModeState(next); }} style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 8, border: "1px solid var(--border-subtle)", background: agiMode === "autonomous" ? "rgba(34,197,94,0.10)" : "var(--bg-overlay)", color: agiMode === "autonomous" ? "var(--status-ok)" : "var(--text-secondary)", cursor: "pointer", fontSize: "0.7rem", fontWeight: 600 }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: agiMode === "autonomous" ? "var(--status-ok)" : "var(--text-tertiary)", display: "inline-block" }} />
                        {agiMode === "autonomous" ? "Auto" : "Manual"}
                    </button>
                    <button onClick={() => setDarkMode(d => !d)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", padding: 5, borderRadius: 6, display: "flex" }}>
                        {darkMode ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" /></svg>
                            : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" /></svg>}
                    </button>
                    <button onClick={() => { setApiKey(""); setApiKeyState(""); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", fontSize: "0.7rem", padding: "3px 8px", borderRadius: 6 }}>Sign out</button>
                </div>
            </header>

            {/* Body */}
            <div style={{ display: "flex", flex: 1, overflow: "hidden", position: "relative" }}>
                {sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 150, background: "rgba(0,0,0,0.4)" }} />}

                {/* Sidebar */}
                <div style={{ position: "fixed", top: 0, left: 0, bottom: 0, width: 216, paddingTop: "calc(52px + 28px)", background: "var(--bg-surface)", borderRight: "1px solid var(--border-subtle)", padding: "80px 8px 12px", display: "flex", flexDirection: "column", gap: 3, zIndex: 151, transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)", transition: "transform 0.22s cubic-bezier(0.4,0,0.2,1)", overflowY: "auto" }}>
                    <button onClick={() => { setMessages([]); setView("chat"); setSidebarOpen(false); }} style={{ padding: "8px 12px", borderRadius: 8, background: "var(--brand)", color: "#fff", border: "none", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600, marginBottom: 8 }}>+ New Deliberation</button>
                    {navBtn("chat", <><svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="3" /><circle cx="8" cy="8" r="7" strokeDasharray="2 2" strokeOpacity=".4" /></svg>Deliberate</>)}
                    {navBtn("signals", <><svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 8.5l4 1.5L8 14l1.5-4L13 2z" /></svg>Signals</>, signalCount || undefined)}
                    {navBtn("goals", <><svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6.5" /><circle cx="8" cy="8" r="3" /><circle cx="8" cy="8" r="1" fill="currentColor" stroke="none" /></svg>Goal Registry</>)}
                    {navBtn("projects", <><svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M1 3.5A1.5 1.5 0 012.5 2H6l1.5 2H14a1 1 0 011 1v7a1 1 0 01-1 1H2a1 1 0 01-1-1V3.5z" /></svg>Projects</>)}
                    {navBtn("history", <><svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6.5" /><path d="M8 4.5V8l2.5 2" /></svg>History</>)}
                    {activeProject && (
                        <div style={{ marginTop: 10, padding: "6px 12px", borderRadius: 8, background: "var(--bg-overlay)", fontSize: "0.75rem" }}>
                            <div style={{ color: "var(--text-tertiary)", fontSize: "0.63rem", fontFamily: "var(--font-mono)" }}>ACTIVE PROJECT</div>
                            <div style={{ color: "var(--text-primary)", fontWeight: 600, marginTop: 2 }}>{activeProject.name}</div>
                            <button onClick={() => setActiveProject(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", fontSize: "0.63rem", padding: 0, marginTop: 2 }}>× clear</button>
                        </div>
                    )}
                    <div style={{ marginTop: "auto", padding: "8px 12px", fontSize: "0.63rem", color: "var(--text-tertiary)", fontFamily: "var(--font-mono)", borderTop: "1px solid var(--border-subtle)", paddingTop: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <svg width="6" height="6" viewBox="0 0 6 6"><circle cx="3" cy="3" r="3" fill={agiMode === "autonomous" ? "var(--status-ok)" : "var(--text-tertiary)"} /></svg>
                            {agiMode === "autonomous" ? "AUTONOMOUS MODE" : "MANUAL MODE"}
                        </div>
                        {substrate && <div style={{ marginTop: 6, lineHeight: 1.6 }}>CHAIN: {substrate.chain_depth}<br />{substrate.verified ? "? VERIFIED" : "? UNVERIFIED"}</div>}
                    </div>
                </div>

                {/* Main */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
                    {view === "signals" && <div style={{ flex: 1, overflowY: "auto" }}><SignalsPanel /></div>}
                    {view === "goals" && <div style={{ flex: 1, overflowY: "auto" }}><GoalsPanel /></div>}
                    {view === "projects" && <div style={{ flex: 1, overflowY: "auto" }}><ProjectsPanel onSelect={(p) => { setActiveProject(p); setView("chat"); }} /></div>}
                    {view === "history" && <div style={{ flex: 1, overflowY: "auto" }}><HistoryPanel onLoad={(q, d) => { setDepth(d); setInput(q); setView("chat"); setSidebarOpen(false); }} /></div>}
                    {view === "chat" && (
                        <>
                            <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px 130px", display: "flex", flexDirection: "column", position: "relative" }}>
                                {messages.length === 0
                                    ? <EmptyState substrate={substrate} depth={depth} signalCount={signalCount} />
                                    : messages.map((msg, i) => <MessageBubble key={msg.id} msg={msg} isLatest={i === messages.length - 1} />)
                                }
                                <div ref={messagesEndRef} />
                            </div>
                            {/* Floating input */}
                            <div className="float-anchor">
                                <div className="float-input">
                                    {activeProject && (
                                        <div className="float-input-project">
                                            <span className="float-input-project-label">PROJECT:</span>
                                            <span className="float-input-project-name">{activeProject.name}</span>
                                            <button className="float-input-project-clear" onClick={() => setActiveProject(null)}>×</button>
                                        </div>
                                    )}
                                    <div className="float-input-row">
                                        <textarea
                                            ref={inputRef}
                                            className="float-input-textarea"
                                            value={input}
                                            onChange={(e) => setInput(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                                            placeholder={depth === "DEEP_RESEARCH" ? "Deep research question…" : depth === "ANALYTICAL" ? "Question for full 3-channel deliberation…" : "Quick question…"}
                                            rows={1}
                                            onInput={(e) => { const t = e.currentTarget; t.style.height = "auto"; t.style.height = Math.min(t.scrollHeight, 160) + "px"; }}
                                            disabled={sending}
                                        />
                                        <button
                                            className="float-input-send"
                                            onClick={handleSend}
                                            disabled={!input.trim() || sending}
                                        >
                                            {sending ? "…" : "→"}
                                        </button>
                                    </div>
                                    <div className="float-input-hint">
                                        {DEPTH_INFO[depth].label.toUpperCase()} · {DEPTH_INFO[depth].desc} · Enter to deliberate
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <style>{`@keyframes pulse{0%,100%{opacity:0.3}50%{opacity:1}}`}</style>
        </div>
    );
}
